import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SliceType } from '~/enums/SliceType';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { zIndices } from '../../../eds/src/tokens/zIndices';
import { ChatbotStatusMessage, ChatbotState as State } from './types';

export const initialState: State = {
  activeCitation: null,
  activeSourceIndex: null,
  context: [],
  currentConversationId: null,
  entity: null,
  initialAgent: null,
  isVisible: false,
  messageQueue: {
    queue: [],
    index: 0,
  },
  mode: 'floating',
  selectedContext: null,
  statusMessage: null,
  isTextInputDisabled: false,
  sideContent: {
    isVisible: false,
    selectedTab: 'questions',
  },
  sources: [],
  width: '525px',
  zIndex: zIndices.panel,
};

const closeChatbot = (state: State) => {
  state.activeCitation = null;
  state.entity = null;
  state.selectedContext = null;
  state.isVisible = false;
  state.currentConversationId = null;
  state.initialAgent = null;
  state.sources = [];
  state.isTextInputDisabled = false;
  state.statusMessage = null;
  state.mode = 'floating';
  state.sideContent = {
    isVisible: false,
    selectedTab: 'questions',
  };
  state.messageQueue = {
    queue: [],
    index: 0,
  };
};

export default createSlice({
  name: SliceType.Chatbot,
  initialState,
  selectors: {
    selectActiveCitation: (state: State) => state.activeCitation,
    selectCurrentChatbot: (state: State) => state.entity,
    selectPanelMode: (state: State) => state.mode,
    selectPanelZIndex: (state: State) => state.zIndex,
    selectChatbotContext: (state: State) => state.context,
    selectChatbotSelectedContext: (state: State) => state.selectedContext,
    selectCurrentSource: (state: State) =>
      state.sources[state.activeSourceIndex ?? 0],
    selectSources: (state: State) => state.sources,
    selectIsChatbotVisible: (state: State) => state.isVisible,
    selectIsSideContentVisible: (state: State) => state.sideContent.isVisible,
    selectSideContentTab: (state: State) => state.sideContent.selectedTab,
    selectChatbotWidth: (state: State) => state.width,
    selectIsTextInputDisabled: (state: State) => state.isTextInputDisabled,
    selectStatusMessage: (state: State) => state.statusMessage,
    selectMessageQueue: (state: State) => state.messageQueue.queue,
    selectIsMessageQueueProcessing: (state: State) =>
      state.messageQueue.queue.some(
        (queueMessage) => queueMessage.status !== 'complete',
      ),
    selectIsMessageQueueRunning: (state: State) =>
      state.messageQueue.queue.some(
        (queueMessage) => queueMessage.status === 'running',
      ),
    selectCurrentConversationId: (state: State) => state.currentConversationId,
    selectInitialAgent: (state: State) => state.initialAgent,
    selectProcessingMessage: (state: State) => state.messageQueue.queue[0],
    selectMessageQueueIndex: (state: State) => state.messageQueue.index,
  },
  reducers: {
    // toggle the visibility of the chatbot. It will also set to null the entity if not visible
    clearMessageQueue: (state: State) => {
      state.messageQueue.queue = [];
      state.messageQueue.index = 0;
    },
    closeChatbot: closeChatbot,
    openSideContent: (
      state: State,
      action: PayloadAction<State['sideContent']['selectedTab'] | undefined>,
    ) => {
      const tab = action.payload;
      if (tab) {
        state.sideContent.selectedTab = tab;
      }
      state.sideContent.isVisible = true;
    },
    postMessageToQueue: (
      state: State,
      action: PayloadAction<
        | { id: Uuid; message: string; parentId?: string }
        | { id: Uuid; message: string; parentId?: string }[]
      >,
    ) => {
      if (Array.isArray(action.payload)) {
        const messages = action.payload.map((message) => ({
          ...message,
          status: 'pending' as const,
        }));
        state.messageQueue.queue.push(...messages);
      } else {
        state.messageQueue.queue.push({
          ...action.payload,
          status: 'pending',
        });
      }
    },
    processNextMessage: (state: State) => {
      if (state.messageQueue.queue.length > 0) {
        state.messageQueue.queue[state.messageQueue.index].status = 'complete';
        if (state.messageQueue.index < state.messageQueue.queue.length - 1) {
          state.messageQueue.index += 1;
        }
      }
    },
    removeMessageFromQueue: (state: State) => {
      state.messageQueue.queue = state.messageQueue.queue.slice(1);
    },
    setActiveSourceIndex: (
      state: State,
      action: PayloadAction<Nullable<number>>,
    ) => {
      state.activeSourceIndex = action.payload;
    },
    clearSources: (state: State) => {
      state.sources = [];
      state.activeSourceIndex = null;
    },
    setActiveCitation: (
      state: State,
      action: PayloadAction<State['activeCitation']>,
    ) => {
      state.activeCitation = action.payload;
    },
    setIsTextInputDisabled: (state: State, action: PayloadAction<boolean>) => {
      state.isTextInputDisabled = action.payload;
    },
    setContext: (state: State, action: PayloadAction<State['context']>) => {
      state.context = action.payload;
    },
    setStatusMessage: (
      state: State,
      action: PayloadAction<Nullable<ChatbotStatusMessage>>,
    ) => {
      state.statusMessage = action.payload;
    },
    setCurrentConversationId: (
      state: State,
      action: PayloadAction<State['currentConversationId']>,
    ) => {
      state.currentConversationId = action.payload;
    },
    setEntity(state: State, action: PayloadAction<State['entity']>) {
      if (!state.entity) {
        state.entity = action.payload;
      }
    },
    overrideEntity(state: State, action: PayloadAction<State['entity']>) {
      state.entity = action.payload;
    },
    setPanelMode: (state: State, action: PayloadAction<State['mode']>) => {
      state.mode = action.payload;
    },
    setPanelZIndex: (state: State, action: PayloadAction<State['zIndex']>) => {
      state.zIndex = action.payload;
    },
    setSelectedContextInterim: (
      state: State,
      action: PayloadAction<State['selectedContext']>,
    ) => {
      if (state.entity?.type === 'document_version' && action.payload) {
        state.entity = {
          type: 'all_documents',
          id: action.payload.id,
        };
      }
      state.selectedContext = action.payload;
    },
    setSelectedContext: (
      state: State,
      action: PayloadAction<State['selectedContext']>,
    ) => {
      state.selectedContext = action.payload;
    },
    setSideContentTab: (
      state: State,
      action: PayloadAction<State['sideContent']['selectedTab']>,
    ) => {
      state.sideContent.selectedTab = action.payload;
    },
    setSources: (state: State, action: PayloadAction<State['sources']>) => {
      state.sources = action.payload;
    },
    setWidth: (state: State, action: PayloadAction<State['width']>) => {
      state.width = action.payload;
    },
    setInitialAgent: (
      state: State,
      action: PayloadAction<State['initialAgent']>,
    ) => {
      state.initialAgent = action.payload;
    },
    toggleChatbot: (state: State) => {
      if (state.isVisible) {
        closeChatbot(state);
      } else {
        state.isVisible = true;
        state.selectedContext =
          state.context.find((item) => item.primary) ?? null;
      }
    },
    toggleSideContent: (state: State) => {
      state.sideContent.isVisible = !state.sideContent.isVisible;
    },
    updateMessageStatus: (
      state: State,
      action: PayloadAction<{
        status: 'complete' | 'running' | 'pending';
        id: Uuid;
      }>,
    ) => {
      const index = state.messageQueue.queue.findIndex(
        (message) => message.id === action.payload.id,
      );
      if (index >= 0) {
        state.messageQueue.queue[index].status = action.payload.status;
      }
    },
  },
});
